import { useEffect } from 'react'

/**
 * A custom React hook designed to improve the user experience of the Ant Design DatePicker
 * and TimePicker components, particularly in mobile and hybrid input scenarios.
 *
 * 📌 Purpose
 * Automatically triggers the "OK" button of the AntD Date/Time picker when a user selects
 * a date or time, then interacts outside the dropdown. This reduces friction, especially
 * on mobile devices, by eliminating the need to explicitly tap "OK" after selection.
 *
 * 🧠 Background
 * Based on BuildPass user feedback, requiring an extra tap on "OK" was unintuitive—especially
 * on mobile. This hook streamlines the flow as part of the Date & Time Picker Revamp.
 *
 * ⚙️ How It Works
 * - Observes mutations to detect `.ant-picker-dropdown` being added.
 * - On detection, it:
 *   - Locates the OK button inside the dropdown.
 *   - Attaches click listeners to time/date cells to set a pending commit flag.
 *   - Monitors interactions outside the dropdown (click, touch, pointer).
 *   - If a commit is pending and interaction occurs outside, it auto-clicks the OK button.
 *
 * 🧼 Cleanup
 * - Disconnects the MutationObserver on unmount.
 * - Removes all global event listeners safely.
 *
 * ⚠️ Limitations
 * - Depends on Ant Design class names and DOM structure, which may change in future versions.
 * - Assumes dropdowns are appended to document.body (AntD default).
 *
 * 🧩 Integration
 * Just call this hook once at the root of a component using DatePicker/TimePicker.
 * It doesn't return anything or require props.
 */

export const useAntDPickerAutoOk = () => {
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const dropdowns = document.querySelectorAll('.ant-picker-dropdown')

      dropdowns.forEach((dropdown) => {
        if (!(dropdown instanceof HTMLElement)) return

        const okBtn = dropdown.querySelector(
          '.ant-picker-ok button'
        ) as HTMLButtonElement | null

        if (!okBtn) return

        let hasPendingCommit = false
        let currentDropdown: HTMLElement | null = dropdown

        const triggerCommitIfNeeded = () => {
          if (currentDropdown && hasPendingCommit) {
            const ok = currentDropdown.querySelector(
              '.ant-picker-ok button'
            ) as HTMLButtonElement
            if (ok) {
              ok.click()
            }
            hasPendingCommit = false
            currentDropdown = null
          }
        }

        const onInteractionOutside = (event: Event) => {
          if (!currentDropdown) return

          // For touch events, we need to check the first touch point
          const target =
            event.type.startsWith('touch') && 'touches' in event
              ? ((event as TouchEvent).touches[0]?.target as Node)
              : (event.target as Node)

          if (!currentDropdown.contains(target) && hasPendingCommit) {
            setTimeout(() => {
              triggerCommitIfNeeded()
            }, 0)

            removeAllEventListeners()
          }
        }

        const removeAllEventListeners = () => {
          document.removeEventListener('mousedown', onInteractionOutside)
          document.removeEventListener('touchstart', onInteractionOutside)
          document.removeEventListener('touchend', onInteractionOutside)
          document.removeEventListener('pointerdown', onInteractionOutside)
        }

        const setUpAutoCommit = () => {
          hasPendingCommit = true
          currentDropdown = dropdown

          removeAllEventListeners()

          document.addEventListener('mousedown', onInteractionOutside)
          document.addEventListener('touchstart', onInteractionOutside)
          document.addEventListener('touchend', onInteractionOutside)
          document.addEventListener('pointerdown', onInteractionOutside)
        }

        // Rebind date and time cell events every time
        const timePanel = dropdown.querySelector('.ant-picker-time-panel')
        const timeCells =
          timePanel?.querySelectorAll('.ant-picker-time-panel-cell') || []
        timeCells.forEach((cell) => {
          cell.removeEventListener('click', setUpAutoCommit) // ensure no duplicate
          cell.addEventListener('click', setUpAutoCommit)
        })

        const dateCells = dropdown.querySelectorAll('.ant-picker-cell')
        dateCells.forEach((cell) => {
          cell.removeEventListener('click', setUpAutoCommit)
          cell.addEventListener('click', setUpAutoCommit)
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
      // Use empty functions to avoid errors if the event listeners weren't added
      document.removeEventListener('mousedown', () => {})
      document.removeEventListener('touchstart', () => {})
      document.removeEventListener('touchend', () => {})
      document.removeEventListener('pointerdown', () => {})
    }
  }, [])
}
